import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateResellerInput,
  CreateResellerPlanInput,
  ResellerCeilingReportDto,
  ResellerDto,
  ResellerPlanDto,
  ResellerScopeDto,
  UpdateResellerInput,
  UpdateResellerPlanInput,
} from '@cpm/shared';
import { ApiClientError, api } from './client.js';

/**
 * The current reseller scope (G20): non-null for a reseller login or an admin
 * impersonating one. 404 (no scope) resolves to `null` so the impersonation
 * banner simply hides.
 */
export function useResellerScope() {
  return useQuery({
    queryKey: ['reseller', 'me'],
    queryFn: async (): Promise<ResellerScopeDto | null> => {
      try {
        return await api.get<ResellerScopeDto>('/api/resellers/me');
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) return null;
        if (err instanceof ApiClientError && (err.status === 401 || err.status === 403)) return null;
        throw err;
      }
    },
    staleTime: 30_000,
  });
}

/** After entering/leaving impersonation, every scoped view must refetch. */
function resetScopedCaches(queryClient: ReturnType<typeof useQueryClient>) {
  for (const key of [['reseller', 'me'], ['clients'], ['plans'], ['subscriptions'], ['system', 'nav']]) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

export function useStartImpersonation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resellerId: string) =>
      api.post<ResellerScopeDto>(`/api/resellers/${resellerId}/impersonate`, {}),
    onSuccess: () => resetScopedCaches(queryClient),
  });
}

export function useStopImpersonation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/resellers/impersonate/stop', {}),
    onSuccess: () => resetScopedCaches(queryClient),
  });
}

export function useResellers() {
  return useQuery({
    queryKey: ['resellers'],
    queryFn: () => api.get<ResellerDto[]>('/api/resellers'),
  });
}

export function useResellerPlans() {
  return useQuery({
    queryKey: ['resellers', 'plans'],
    queryFn: () => api.get<ResellerPlanDto[]>('/api/resellers/plans'),
  });
}

export function useResellerReport(id: string | null) {
  return useQuery({
    queryKey: ['resellers', 'report', id],
    queryFn: () => api.get<ResellerCeilingReportDto>(`/api/resellers/${id}/report`),
    enabled: id != null,
  });
}

export function useCreateResellerPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateResellerPlanInput) =>
      api.post<ResellerPlanDto>('/api/resellers/plans', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['resellers', 'plans'] }),
  });
}

export function useUpdateResellerPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateResellerPlanInput }) =>
      api.patch<ResellerPlanDto>(`/api/resellers/plans/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['resellers', 'plans'] });
      void queryClient.invalidateQueries({ queryKey: ['resellers'] });
    },
  });
}

export function useDeleteResellerPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/resellers/plans/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['resellers', 'plans'] }),
  });
}

export function useCreateReseller() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateResellerInput) => api.post<ResellerDto>('/api/resellers', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['resellers'] }),
  });
}

export function useUpdateReseller() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateResellerInput }) =>
      api.patch<ResellerDto>(`/api/resellers/${id}`, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['resellers'] }),
  });
}

export function useDeleteReseller() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/resellers/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['resellers'] }),
  });
}
